import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, UtensilsCrossed, Clock, DollarSign, Layers, Palette } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { key: 'tipos', label: 'Tipos de Servicio' },
  { key: 'centros', label: 'Centros de Costo' },
];

const COLORES = ['emerald', 'amber', 'lime', 'sky', 'indigo', 'violet', 'rose', 'orange'];

// Tailwind no genera clases dinámicas — mapa explícito de estilos por color.
const COLOR_HEX = {
  emerald: '#10b981', amber: '#f59e0b', lime: '#84cc16', sky: '#0ea5e9',
  indigo: '#6366f1', violet: '#8b5cf6', rose: '#f43f5e', orange: '#f97316',
};

const fmtCLP = (n) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n || 0);

export default function SgCasinoMantenedor() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('tipos');
  const [tipos, setTipos] = useState([]);
  const [centros, setCentros] = useState([]);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rt, rc] = await Promise.all([
        axios.get(`${API}/servicios-generales/casino/tipos-servicio`, { headers }),
        axios.get(`${API}/servicios-generales/casino/centros-costo`, { headers }),
      ]);
      setTipos(rt.data);
      setCentros(rc.data);
    } catch (_) { /* noop */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const del = async (kind, id) => {
    const path = kind === 'tipos' ? 'tipos-servicio' : 'centros-costo';
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await axios.delete(`${API}/servicios-generales/casino/${path}/${id}`, { headers });
      toast.success('Eliminado.'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="sg-casino-mant-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Casino — Mantenedores</h1>
          <p className="text-sm text-slate-500">Tipos de servicio con horario y valor, y centros de costo para facturación por contrato.</p>
        </div>
        <button onClick={() => setModal({ mode: 'create', kind: tab })}
          className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-lg"
          data-testid="btn-nuevo-casino">
          <Plus className="w-4 h-4" /> Nuevo
        </button>
      </div>

      <div className="inline-flex bg-white border border-slate-200 rounded-lg p-0.5">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${tab === t.key ? 'bg-emerald-700 text-white shadow' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-6">Cargando…</div>
      ) : tab === 'tipos' ? (
        <TablaTipos rows={tipos} onEdit={r => setModal({ mode: 'edit', kind: 'tipos', data: r })} onDelete={r => del('tipos', r.id)} />
      ) : (
        <TablaCentros rows={centros} onEdit={r => setModal({ mode: 'edit', kind: 'centros', data: r })} onDelete={r => del('centros', r.id)} />
      )}

      {modal && (
        modal.kind === 'tipos'
          ? <ModalTipo modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} headers={headers} />
          : <ModalCentro modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} headers={headers} />
      )}
    </div>
  );
}

function TablaTipos({ rows, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>
            <th className="text-center px-3 py-2 font-semibold w-14">Orden</th>
            <th className="text-left px-4 py-2 font-semibold">Servicio</th>
            <th className="text-left px-4 py-2 font-semibold">Horario</th>
            <th className="text-right px-4 py-2 font-semibold">Valor</th>
            <th className="text-center px-4 py-2 font-semibold">Activo</th>
            <th className="text-right px-4 py-2 font-semibold">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="text-center text-slate-400 py-8">Sin tipos de servicio.</td></tr>
          ) : rows.map(t => (
            <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50">
              <td className="px-3 py-2 text-center text-slate-400 text-xs">{t.orden}</td>
              <td className="px-4 py-2 font-bold text-slate-800">
                <span className={`inline-flex items-center gap-2`}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR_HEX[t.color] || COLOR_HEX.emerald }} />
                  {t.nombre}
                </span>
              </td>
              <td className="px-4 py-2 text-slate-600 font-mono">
                {t.hora_inicio && t.hora_fin ? `${t.hora_inicio} — ${t.hora_fin}` : '—'}
              </td>
              <td className="px-4 py-2 text-right font-semibold text-slate-800">{fmtCLP(t.valor_clp)}</td>
              <td className="px-4 py-2 text-center">
                {t.activo
                  ? <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">ACTIVO</span>
                  : <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">INACTIVO</span>}
              </td>
              <td className="px-4 py-2 text-right whitespace-nowrap">
                <button onClick={() => onEdit(t)} className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => onDelete(t)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TablaCentros({ rows, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>
            <th className="text-left px-4 py-2 font-semibold">Código</th>
            <th className="text-left px-4 py-2 font-semibold">Nombre</th>
            <th className="text-left px-4 py-2 font-semibold">Descripción</th>
            <th className="text-center px-4 py-2 font-semibold">Activo</th>
            <th className="text-right px-4 py-2 font-semibold">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={5} className="text-center text-slate-400 py-8">Sin centros de costo.</td></tr>
          ) : rows.map(c => (
            <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/50">
              <td className="px-4 py-2 font-mono text-slate-700 font-bold">{c.codigo}</td>
              <td className="px-4 py-2 text-slate-800">{c.nombre}</td>
              <td className="px-4 py-2 text-slate-500 text-xs">{c.descripcion || '—'}</td>
              <td className="px-4 py-2 text-center">
                {c.activo
                  ? <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">ACTIVO</span>
                  : <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">INACTIVO</span>}
              </td>
              <td className="px-4 py-2 text-right whitespace-nowrap">
                <button onClick={() => onEdit(c)} className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => onDelete(c)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ModalTipo({ modal, onClose, onSaved, headers }) {
  const isEdit = modal.mode === 'edit';
  const d = modal.data;
  const [form, setForm] = useState({
    nombre: d?.nombre || '',
    valor_clp: d?.valor_clp ?? 0,
    hora_inicio: d?.hora_inicio || '',
    hora_fin: d?.hora_fin || '',
    orden: d?.orden ?? 0,
    color: d?.color || 'emerald',
    activo: d?.activo ?? true,
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error('Nombre obligatorio'); return; }
    try {
      const payload = { ...form, valor_clp: Number(form.valor_clp), orden: Number(form.orden) };
      if (isEdit) await axios.put(`${API}/servicios-generales/casino/tipos-servicio/${d.id}`, payload, { headers });
      else await axios.post(`${API}/servicios-generales/casino/tipos-servicio`, payload, { headers });
      toast.success('Guardado.'); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2"><UtensilsCrossed className="w-5 h-5 text-emerald-700" /> {isEdit ? 'Editar' : 'Nuevo'} tipo de servicio</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-slate-500 font-semibold">Nombre *</label>
            <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} autoFocus
              placeholder="Desayuno, Almuerzo, Colación…"
              className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-tipo-nombre" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Clock className="w-3 h-3" /> Hora inicio</label>
              <input type="time" value={form.hora_inicio} onChange={e => setForm({ ...form, hora_inicio: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Clock className="w-3 h-3" /> Hora fin</label>
              <input type="time" value={form.hora_fin} onChange={e => setForm({ ...form, hora_fin: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><DollarSign className="w-3 h-3" /> Valor CLP</label>
              <input type="number" min="0" value={form.valor_clp} onChange={e => setForm({ ...form, valor_clp: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-tipo-valor" />
              <p className="text-[10px] text-slate-400 mt-0.5">{fmtCLP(Number(form.valor_clp) || 0)}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Layers className="w-3 h-3" /> Orden</label>
              <input type="number" value={form.orden} onChange={e => setForm({ ...form, orden: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Palette className="w-3 h-3" /> Color</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {COLORES.map(c => (
                <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                  style={{ background: COLOR_HEX[c] }}
                  className={`w-8 h-8 rounded-full border-2 ${form.color === c ? 'border-slate-900 ring-2 ring-offset-1 ring-slate-400' : 'border-white'}`}
                  title={c} />
              ))}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} />
            Activo
          </label>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold" data-testid="btn-guardar-tipo">
              {isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalCentro({ modal, onClose, onSaved, headers }) {
  const isEdit = modal.mode === 'edit';
  const d = modal.data;
  const [form, setForm] = useState({
    codigo: d?.codigo || '',
    nombre: d?.nombre || '',
    descripcion: d?.descripcion || '',
    activo: d?.activo ?? true,
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.codigo.trim() || !form.nombre.trim()) { toast.error('Código y nombre obligatorios'); return; }
    try {
      if (isEdit) await axios.put(`${API}/servicios-generales/casino/centros-costo/${d.id}`, form, { headers });
      else await axios.post(`${API}/servicios-generales/casino/centros-costo`, form, { headers });
      toast.success('Guardado.'); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{isEdit ? 'Editar' : 'Nuevo'} centro de costo</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-[140px_1fr] gap-3">
            <div>
              <label className="text-xs text-slate-500 font-semibold">Código *</label>
              <input value={form.codigo} onChange={e => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                placeholder="CC-100" autoFocus
                className="w-full px-3 py-2 text-sm border rounded-lg font-mono" data-testid="input-centro-codigo" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold">Nombre *</label>
              <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
                placeholder="Operaciones Mina"
                className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-centro-nombre" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Descripción</label>
            <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={2}
              placeholder="Notas o detalles del centro de costo"
              className="w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} />
            Activo
          </label>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold" data-testid="btn-guardar-centro">
              {isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
